import type { InventoryListing } from "@workspace/api-client-react";

// Centralized per-ticket transfer-link coverage math, shared by the inventory
// table cell, its hover detail, and the CSV export so the three never drift.
//
// The numerator (tickets WITH a link) and the denominator (tickets that NEED a
// link) are ALWAYS computed from the same ticket set. Otherwise a listing whose
// only linked tickets are sold could divide a full numerator by a smaller
// "unsold" denominator and read as full coverage.
//
// Which tickets need a link:
//   - buy-back candidate (sold-but-unfulfilled): ALL of its tickets, because the
//     sold tickets are exactly the ones awaiting a re-delivery link. Measuring
//     against unsold tickets would divide by zero for a fully-sold row.
//   - normal remaining inventory: mirrors QrCoverageCell — unsold tickets only
//     when any are sold (a sold ticket there never needs a link), else all.
export function linkCoverageStats(row: InventoryListing) {
  const assets = row.linkAssets ?? [];
  const total = assets.length;
  const hasSold = assets.some((a) => a.sold);
  const needing = row.buybackCandidate
    ? assets
    : hasSold
      ? assets.filter((a) => !a.sold)
      : assets;
  // Fall back to the whole listing when the "needing" set is empty so the
  // denominator is never zero.
  const base = needing.length > 0 ? needing : assets;
  const denominator = base.length;
  const withLink = base.filter((a) => a.hasLink).length;
  // Only normal inventory describes its denominator as "unsold" tickets; a
  // buy-back row measures coverage across its (sold) tickets.
  const qualifier = hasSold && !row.buybackCandidate ? "unsold " : "";
  return { assets, total, hasSold, denominator, withLink, qualifier };
}
